/**
 * CallContext
 * Full WebRTC call lifecycle: signaling, peer connection, mic capture,
 * voice isolation, hold, DND, quality monitoring, history logging,
 * missed-call notifications.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { CallSignalingService, type SignalMessage } from '../../services/CallSignalingService';
import { CallRingService } from '../../services/CallRingService';
import { isIOSNativeShell } from '../../utils/nativeApp';
import { DataService } from '../../services/DataService';
import { checkWorkingHours } from '../../hooks/useWorkingHoursStatus';
import { CallAudioService } from '../../services/CallAudioService';
import { supabase } from '../../utils/supabase/client';
import { NativeBridge } from '../../services/NativeBridge';
import { MessagingService } from '../../services/MessagingService';
import type { User } from '../../types/auth';

// ─── ICE config ──────────────────────────────────────────────────────────────

function buildICEServers(): RTCIceServer[] {
  const servers: RTCIceServer[] = [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
  ];
  // TURN relay — provide via .env:
  //   VITE_TURN_URL       e.g. turn:your-server.com:3478
  //   VITE_TURN_USERNAME  turn username
  //   VITE_TURN_CREDENTIAL  turn credential
  const turnUrl = import.meta.env.VITE_TURN_URL as string | undefined;
  if (turnUrl) {
    servers.push({
      urls: turnUrl,
      username: (import.meta.env.VITE_TURN_USERNAME as string) ?? '',
      credential: (import.meta.env.VITE_TURN_CREDENTIAL as string) ?? '',
    });
  }
  return servers;
}

const CALL_TIMEOUT_MS = 40_000;

// ─── Types ────────────────────────────────────────────────────────────────────

export type CallStatus   = 'idle' | 'outgoing' | 'incoming' | 'connected' | 'voicemail';
export type MicError     = 'denied' | 'unavailable' | null;
export type CallQuality  = 'good' | 'fair' | 'poor' | null;
export type Availability = 'available' | 'dnd';
type CallOutcome = 'connected' | 'no_answer' | 'rejected' | 'busy' | 'failed';

interface CallContextValue {
  status: CallStatus;
  remoteUser: User | null;
  isMuted: boolean;
  isOnHold: boolean;       // we put the call on hold
  remoteOnHold: boolean;   // remote party put us on hold
  elapsedSeconds: number;
  micError: MicError;
  quality: CallQuality;
  availability: Availability;
  /** Available audio output devices (populated after mic permission granted). */
  outputDevices: MediaDeviceInfo[];
  /** Currently selected output device ID ('default' = system default). */
  outputDeviceId: string;
  /** Whether setSinkId is supported on this platform (Chrome/Edge desktop). */
  sinkIdSupported: boolean;

  startCall: (user: User) => void;
  /** Reserve audio devices while a channel huddle is connecting or active. */
  setExternalCallActive: (active: boolean) => void;
  answerCall: () => void;
  rejectCall: () => void;
  endCall: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  setAvailability: (a: Availability) => void;
  clearMicError: () => void;
  /** Change the audio output device (web) or route (native). */
  setOutputDevice: (deviceId: string) => void;
  /** Upload a recorded voicemail blob and send it as a message to the callee. */
  sendVoicemail: (blob: Blob, durationSeconds: number) => Promise<void>;
  /** Cancel out of voicemail mode without sending. */
  cancelVoicemail: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const CallContext = createContext<CallContextValue | null>(null);

// Shell chrome can render before a full workspace session exists.
export function useOptionalCall(): CallContextValue | null {
  return useContext(CallContext);
}

export function useCall(): CallContextValue {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('useCall must be used within <CallProvider>');
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function CallProvider({ currentUser, children }: { currentUser: User; children: ReactNode }) {
  const [status, setStatus]             = useState<CallStatus>('idle');
  const [remoteUser, setRemoteUser]     = useState<User | null>(null);
  const [isMuted, setIsMuted]           = useState(false);
  const [isOnHold, setIsOnHold]         = useState(false);
  const [remoteOnHold, setRemoteOnHold] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [micError, setMicError]         = useState<MicError>(null);
  const [quality, setQuality]           = useState<CallQuality>(null);
  const [availability, setAvailabilityState] = useState<Availability>(() => {
    // Prefer the DB value (comes in via currentUser.call_availability) so the
    // setting syncs across devices. Fall back to localStorage for the first
    // render before auth hydrates, then fall back to 'available'.
    const fromUser = (currentUser as any).call_availability as Availability | undefined;
    const fromStorage = localStorage.getItem('qs-call-availability') as Availability | null;
    return fromUser ?? fromStorage ?? 'available';
  });
  const [outputDevices, setOutputDevices]   = useState<MediaDeviceInfo[]>([]);
  const [outputDeviceId, setOutputDeviceId] = useState<string>('default');
  const [sinkIdSupported] = useState<boolean>(
    () => typeof document !== 'undefined' && 'setSinkId' in document.createElement('audio'),
  );

  // ── Non-rendering refs ────────────────────────────────────────────────────
  const pcRef               = useRef<RTCPeerConnection | null>(null);
  const externalCallActiveRef = useRef(false);
  const setExternalCallActive = useCallback((active: boolean) => {
    externalCallActiveRef.current = active;
  }, []);
  const localStreamRef      = useRef<MediaStream | null>(null);
  const audioCtxRef         = useRef<AudioContext | null>(null);
  const remoteAudioRef      = useRef<HTMLAudioElement | null>(null);
  const callIdRef           = useRef('');
  const remoteUserRef       = useRef<User | null>(null);
  const statusRef           = useRef<CallStatus>('idle');
  const availabilityRef     = useRef<Availability>(availability);
  const connectedAtRef      = useRef<number | null>(null);
  const callStartedAtRef    = useRef<number | null>(null);
  const outcomeRef          = useRef<CallOutcome | null>(null);
  const pendingCandidates   = useRef<RTCIceCandidateInit[]>([]);
  const timeoutRef          = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerRef            = useRef<ReturnType<typeof setInterval> | null>(null);
  const qualityTimerRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCallerRef         = useRef(false); // did we initiate this call?

  // Sync refs
  useEffect(() => { remoteUserRef.current = remoteUser; }, [remoteUser]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { availabilityRef.current = availability; }, [availability]);

  // ── Call history logging ──────────────────────────────────────────────────

  const logCallHistory = useCallback(async (outcome: CallOutcome, durationSeconds = 0) => {
    const callId = callIdRef.current;
    const remote = remoteUserRef.current;
    if (!callId || !remote || !isCallerRef.current) return; // only caller logs
    // Demo: never write to the shared call_history table.
    if (DataService.isDemoMode()) return;
    try {
      await supabase.from('call_history').insert({
        call_id:          callId,
        caller_id:        currentUser.id,
        callee_id:        remote.id,
        outcome,
        started_at:       callStartedAtRef.current
          ? new Date(callStartedAtRef.current).toISOString()
          : new Date().toISOString(),
        connected_at:     connectedAtRef.current
          ? new Date(connectedAtRef.current).toISOString()
          : null,
        ended_at:         new Date().toISOString(),
        duration_seconds: durationSeconds,
      });
    } catch {
      // Non-critical — never let history logging break the call flow
    }
  }, [currentUser.id]);

  // ── Teardown ─────────────────────────────────────────────────────────────

  const teardown = useCallback((outcome?: CallOutcome) => {
    // Guard against double-teardown: capture & clear callId immediately so a
    // concurrent second call sees an empty callId and logs nothing.
    const callId = callIdRef.current;
    if (!callId && statusRef.current === 'idle') return; // already torn down
    callIdRef.current = '';

    const finalOutcome = outcome ?? outcomeRef.current ?? 'failed';
    const duration = connectedAtRef.current
      ? Math.floor((Date.now() - connectedAtRef.current) / 1000)
      : 0;

    // Pass captured callId directly so the async log uses the right value
    if (callId && isCallerRef.current && remoteUserRef.current) {
      logCallHistory(finalOutcome, duration);
    }

    if (timeoutRef.current)    { clearTimeout(timeoutRef.current);    timeoutRef.current = null; }
    if (timerRef.current)      { clearInterval(timerRef.current);      timerRef.current = null; }
    if (qualityTimerRef.current) { clearInterval(qualityTimerRef.current); qualityTimerRef.current = null; }

    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    CallAudioService.stop();
    deactivateNativeAudioSession();
    connectedAtRef.current = null;
    callStartedAtRef.current = null;
    outcomeRef.current    = null;
    isCallerRef.current   = false;
    pendingCandidates.current = [];

    setStatus('idle');
    setRemoteUser(null);
    setIsMuted(false);
    setIsOnHold(false);
    setRemoteOnHold(false);
    setElapsedSeconds(0);
    setQuality(null);
  }, [logCallHistory]);

  // ── Signal helpers ────────────────────────────────────────────────────────

  const sendSignal = useCallback(async (
    type: SignalMessage['type'],
    payload?: SignalMessage['payload'],
    withCallerInfo?: boolean,
  ) => {
    const target = remoteUserRef.current;
    if (!target) return;
    await CallSignalingService.send({
      type,
      callId: callIdRef.current,
      fromUserId: currentUser.id,
      toUserId: target.id,
      payload,
      ...(withCallerInfo && {
        callerInfo: {
          name: currentUser.displayName || currentUser.name,
          avatar: currentUser.avatar,
          jobTitle: currentUser.jobTitle || (currentUser as any).position,
        },
      }),
    });
  }, [currentUser]);

  // ── Quality monitoring ────────────────────────────────────────────────────

  const startQualityMonitoring = useCallback(() => {
    if (qualityTimerRef.current) return;
    qualityTimerRef.current = setInterval(async () => {
      const pc = pcRef.current;
      if (!pc) return;
      try {
        const stats = await pc.getStats();
        let packetLossRate = 0;
        let rttMs = 0;
        let hasRTT = false;

        stats.forEach((r: any) => {
          if (r.type === 'inbound-rtp' && r.kind === 'audio') {
            const total = (r.packetsReceived ?? 0) + (r.packetsLost ?? 0);
            if (total > 0) packetLossRate = (r.packetsLost ?? 0) / total;
          }
          if (r.type === 'candidate-pair' && r.state === 'succeeded' && r.currentRoundTripTime) {
            rttMs = r.currentRoundTripTime * 1000;
            hasRTT = true;
          }
        });

        if (packetLossRate > 0.05 || (hasRTT && rttMs > 300)) setQuality('poor');
        else if (packetLossRate > 0.02 || (hasRTT && rttMs > 150)) setQuality('fair');
        else setQuality('good');
      } catch { /* stats not available */ }
    }, 3000);
  }, []);

  // ── Build RTCPeerConnection ────────────────────────────────────────────────

  const buildPeerConnection = useCallback((): RTCPeerConnection => {
    const pc = new RTCPeerConnection({ iceServers: buildICEServers() });

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) sendSignal('ice-candidate', candidate.toJSON());
    };

    pc.ontrack = ({ streams }) => {
      if (!streams[0]) return;
      if (!remoteAudioRef.current) {
        const audio = new Audio();
        audio.autoplay = true;
        remoteAudioRef.current = audio;
      }
      remoteAudioRef.current.srcObject = streams[0];
      // Re-apply preferred output device now that the stream is attached
      applyOutputDeviceToElement();
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        CallAudioService.stop();
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        connectedAtRef.current = Date.now();
        outcomeRef.current = 'connected';
        setStatus('connected');
        startQualityMonitoring();
        timerRef.current = setInterval(() => {
          if (connectedAtRef.current) {
            setElapsedSeconds(Math.floor((Date.now() - connectedAtRef.current) / 1000));
          }
        }, 1000);
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        const wasConnected = outcomeRef.current === 'connected';
        if (wasConnected) CallAudioService.playCallEnded();
        sendSignal('call-end');
        teardown('failed');
      }
    };

    return pc;
  }, [sendSignal, startQualityMonitoring, teardown]);

  // ── ICE candidate queue ───────────────────────────────────────────────────

  const flushPendingCandidates = useCallback(async (pc: RTCPeerConnection) => {
    for (const c of pendingCandidates.current) {
      try { await pc.addIceCandidate(c); } catch { /* ignore */ }
    }
    pendingCandidates.current = [];
  }, []);

  // ── Output device enumeration ─────────────────────────────────────────────
  // Only populated after getUserMedia succeeds (browsers hide labels until then).

  const refreshOutputDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setOutputDevices(all.filter((d) => d.kind === 'audiooutput'));
    } catch { /* not supported */ }
  }, []);

  // Listen for plug/unplug events
  useEffect(() => {
    navigator.mediaDevices?.addEventListener?.('devicechange', refreshOutputDevices);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refreshOutputDevices);
  }, [refreshOutputDevices]);

  // Apply selected device to the remote audio element
  const applyOutputDevice = useCallback((deviceId: string) => {
    const el = remoteAudioRef.current as any;
    if (el && typeof el.setSinkId === 'function') {
      el.setSinkId(deviceId).catch(() => { /* unsupported or denied */ });
    }
  }, []);

  const setOutputDevice = useCallback((deviceId: string) => {
    setOutputDeviceId(deviceId);
    applyOutputDevice(deviceId);
    // On native, map well-known device IDs to audio routes
    if (NativeBridge.isRunningNatively()) {
      if (deviceId === 'speaker')    NativeBridge.setAudioRoute('speaker');
      else if (deviceId === 'bluetooth') NativeBridge.setAudioRoute('bluetooth');
      else                               NativeBridge.setAudioRoute('earpiece');
    }
  }, [applyOutputDevice]);

  // Re-apply device whenever the remote audio element gets a new stream
  // (the element reference persists but srcObject changes on each call)
  const applyOutputDeviceToElement = useCallback(() => {
    if (outputDeviceId !== 'default') applyOutputDevice(outputDeviceId);
  }, [outputDeviceId, applyOutputDevice]);

  // ── Native audio session management ───────────────────────────────────────

  const activateNativeAudioSession = useCallback(() => {
    NativeBridge.setAudioSession('voiceCall');
  }, []);

  const deactivateNativeAudioSession = useCallback(() => {
    NativeBridge.setAudioSession('default');
  }, []);

  // ── Microphone — voice isolation + processing chain ───────────────────────

  const getMicStream = useCallback(async (): Promise<MediaStream> => {
    let raw: MediaStream;
    try {
      raw = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...(({ voiceIsolation: true } as unknown) as object),
          sampleRate: 48000,
          channelCount: 1,
        } as MediaTrackConstraints,
        video: false,
      });
    } catch (err: any) {
      const name = err?.name ?? '';
      setMicError(name === 'NotAllowedError' || name === 'PermissionDeniedError' ? 'denied' : 'unavailable');
      throw err;
    }

    try {
      const ctx = new AudioContext({ sampleRate: 48000 });
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(raw);
      const highPass = ctx.createBiquadFilter();
      highPass.type = 'highpass';
      highPass.frequency.value = 80;
      highPass.Q.value = 0.7;
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = -24;
      compressor.knee.value = 30;
      compressor.ratio.value = 12;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.25;
      const destination = ctx.createMediaStreamDestination();
      source.connect(highPass);
      highPass.connect(compressor);
      compressor.connect(destination);
      localStreamRef.current = raw;
      // Enumerate output devices now that permission has been granted
      void refreshOutputDevices();
      return destination.stream;
    } catch {
      localStreamRef.current = raw;
      void refreshOutputDevices();
      return raw;
    }
  }, [refreshOutputDevices]);

  // ── Incoming signal handler ───────────────────────────────────────────────

  useEffect(() => {
    // Demo session: never join the call signalling channel — the demo
    // shouldn't receive incoming calls from real staff or be paged.
    if (DataService.isDemoMode()) return;
    // In the iOS shell the native CallManager subscribes to this same channel
    // and owns the CallKit UI. Subscribing here too would ring twice on one
    // device, three times once the VoIP push lands.
    if (isIOSNativeShell()) return;
    CallSignalingService.subscribe(currentUser.id);

    const unsub = CallSignalingService.onSignal(async (msg) => {
      switch (msg.type) {

        case 'offer': {
          // DND — redirect caller to voicemail instead of busy
          if (availabilityRef.current === 'dnd') {
            await CallSignalingService.send({
              type: 'call-voicemail',
              callId: msg.callId,
              fromUserId: currentUser.id,
              toUserId: msg.fromUserId,
              // callerInfo carries the DND user's details so the caller can say "X is unavailable"
              callerInfo: {
                name: currentUser.displayName || currentUser.name,
                avatar: currentUser.avatar,
                jobTitle: currentUser.jobTitle || (currentUser as any).position,
              },
            });
            return;
          }

          // Outside working hours — redirect to voicemail if the user has opted in
          {
            const workingSchedule =
              (currentUser as any).working_schedule ??
              (currentUser as any).preferences?.workingSchedule;
            const redirectEnabled = workingSchedule?.redirectCallsOutsideHours !== false;
            if (
              workingSchedule?.days &&
              redirectEnabled &&
              checkWorkingHours(workingSchedule) === 'out_of_hours'
            ) {
              await CallSignalingService.send({
                type: 'call-voicemail',
                callId: msg.callId,
                fromUserId: currentUser.id,
                toUserId: msg.fromUserId,
                callerInfo: {
                  name: currentUser.displayName || currentUser.name,
                  avatar: currentUser.avatar,
                  jobTitle: currentUser.jobTitle || (currentUser as any).position,
                },
              });
              return;
            }
          }
          // Already busy (on a call, outgoing, incoming from someone else, or recording voicemail)
          if (statusRef.current !== 'idle' || externalCallActiveRef.current) {
            const callerName = msg.callerInfo?.name ?? 'Someone';
            const statusLabel: Record<string, string> = {
              connected: 'you\'re on a call',
              outgoing:  'you\'re calling someone',
              incoming:  'your phone is already ringing',
              voicemail: 'you\'re leaving a voicemail',
            };
            const reason = externalCallActiveRef.current ? 'you\'re in a huddle' : (statusLabel[statusRef.current] ?? 'you\'re busy');
            // Notify the busy user non-intrusively
            toast.info(`${callerName} tried to call — ${reason}`, { duration: 5000 });
            // Redirect the caller to voicemail instead of just dropping them
            await CallSignalingService.send({
              type:       'call-voicemail',
              callId:     msg.callId,
              fromUserId: currentUser.id,
              toUserId:   msg.fromUserId,
              callerInfo: {
                name:     currentUser.displayName || currentUser.name,
                avatar:   currentUser.avatar,
                jobTitle: currentUser.jobTitle || (currentUser as any).position,
              },
            });
            return;
          }

          const caller: User = {
            id:          msg.fromUserId,
            name:        msg.callerInfo?.name ?? 'Someone',
            displayName: msg.callerInfo?.name,
            email:       '',
            role:        'staff',
            status:      'active',
            avatar:      msg.callerInfo?.avatar,
            jobTitle:    msg.callerInfo?.jobTitle,
          } as User;

          callIdRef.current = msg.callId;
          isCallerRef.current = false;
          setRemoteUser(caller);
          setStatus('incoming');
          CallAudioService.play('ring');

          pcRef.current = buildPeerConnection();
          await pcRef.current.setRemoteDescription(msg.payload as RTCSessionDescriptionInit);
          await flushPendingCandidates(pcRef.current);

          timeoutRef.current = setTimeout(() => {
            // Auto-decline — don't log history (caller handles it)
            CallAudioService.stop();
            teardown();
          }, CALL_TIMEOUT_MS);
          break;
        }

        case 'answer': {
          if (!pcRef.current || callIdRef.current !== msg.callId) return;
          await pcRef.current.setRemoteDescription(msg.payload as RTCSessionDescriptionInit);
          await flushPendingCandidates(pcRef.current);
          break;
        }

        case 'ice-candidate': {
          if (callIdRef.current !== msg.callId) return;
          const cand = msg.payload as RTCIceCandidateInit;
          if (pcRef.current?.remoteDescription) {
            try { await pcRef.current.addIceCandidate(cand); } catch { /* ignore */ }
          } else {
            pendingCandidates.current.push(cand);
          }
          break;
        }

        case 'call-end': {
          if (callIdRef.current !== msg.callId) return;
          const s = statusRef.current;
          if (s === 'connected' || s === 'outgoing') {
            CallAudioService.playCallEnded();
          }
          if (s === 'incoming') {
            // Caller cancelled before we answered — it's a missed call for us
            const caller = remoteUserRef.current;
            const callerName = caller?.displayName || caller?.name || 'Someone';
            toast.info(`Missed call from ${callerName}`);
          }
          teardown();
          break;
        }

        case 'call-missed': {
          // Explicit missed-call signal from the caller (sent on timeout)
          const callerName = msg.callerInfo?.name ?? 'Someone';
          toast.info(`Missed call from ${callerName}`);
          teardown();
          break;
        }

        case 'call-voicemail': {
          // Callee is on DND — enter voicemail mode on the caller's side
          if (callIdRef.current !== msg.callId) return;
          CallAudioService.stop();
          if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
          // remoteUser already set from startCall; update with any richer info from the signal
          if (msg.callerInfo) {
            setRemoteUser((prev) => prev ? {
              ...prev,
              displayName: msg.callerInfo!.name ?? prev.displayName,
              avatar:      msg.callerInfo!.avatar ?? prev.avatar,
              jobTitle:    msg.callerInfo!.jobTitle ?? (prev as any).jobTitle,
            } as User : prev);
          }
          setStatus('voicemail');
          break;
        }

        case 'call-reject': {
          if (callIdRef.current !== msg.callId) return;
          outcomeRef.current = 'rejected';
          teardown('rejected');
          break;
        }

        case 'call-busy': {
          // Older client sent busy instead of voicemail — redirect locally
          if (callIdRef.current !== msg.callId) return;
          outcomeRef.current = 'busy';
          CallAudioService.stop();
          if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
          setStatus('voicemail');
          break;
        }

        case 'call-hold': {
          if (callIdRef.current !== msg.callId) return;
          setRemoteOnHold(true);
          break;
        }

        case 'call-unhold': {
          if (callIdRef.current !== msg.callId) return;
          setRemoteOnHold(false);
          break;
        }
      }
    });

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.id, statusRef.current]);

  // ── Public API ────────────────────────────────────────────────────────────

  const startCall = useCallback(async (target: User) => {
    if (statusRef.current !== 'idle' || externalCallActiveRef.current) return;
    // Outgoing calls from the iOS shell go through the native path.
    if (isIOSNativeShell()) return;

    const callId = uuidv4();
    callIdRef.current      = callId;
    callStartedAtRef.current = Date.now();
    isCallerRef.current    = true;
    outcomeRef.current     = 'no_answer'; // default until updated

    setRemoteUser(target);
    setStatus('outgoing');
    activateNativeAudioSession();

    let stream: MediaStream;
    try {
      stream = await getMicStream();
    } catch {
      teardown('failed');
      return;
    }

    const pc = buildPeerConnection();
    pcRef.current = pc;
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await CallSignalingService.send({
      type:       'offer',
      callId,
      fromUserId: currentUser.id,
      toUserId:   target.id,
      payload:    pc.localDescription?.toJSON(),
      media:      'audio',
      callerInfo: {
        name:     currentUser.displayName || currentUser.name,
        avatar:   currentUser.avatar,
        jobTitle: currentUser.jobTitle || (currentUser as any).position,
      },
    });

    // Wakes a backgrounded or terminated iPhone. Fired after the offer so the
    // realtime path keeps its head start and usually makes the push a no-op.
    void CallRingService.ring({ callId, calleeUserIds: [target.id], media: 'audio' });

    CallAudioService.play('ringback');

    timeoutRef.current = setTimeout(async () => {
      // Notify callee to stop ringing
      await CallSignalingService.send({
        type:       'call-missed',
        callId,
        fromUserId: currentUser.id,
        toUserId:   target.id,
        callerInfo: {
          name:     currentUser.displayName || currentUser.name,
          avatar:   currentUser.avatar,
          jobTitle: currentUser.jobTitle || (currentUser as any).position,
        },
      });
      timeoutRef.current = null;
      CallAudioService.stop();
      // Instead of hanging up, drop into voicemail so the caller can leave a message
      setStatus('voicemail');
    }, CALL_TIMEOUT_MS);
  }, [currentUser, getMicStream, buildPeerConnection, teardown]);

  const answerCall = useCallback(async () => {
    if (statusRef.current !== 'incoming' || !pcRef.current) return;
    CallAudioService.stop();
    activateNativeAudioSession();
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }

    let stream: MediaStream;
    try {
      stream = await getMicStream();
    } catch {
      await sendSignal('call-reject');
      teardown();
      return;
    }
    stream.getTracks().forEach((t) => pcRef.current!.addTrack(t, stream));
    const answer = await pcRef.current.createAnswer();
    await pcRef.current.setLocalDescription(answer);
    await sendSignal('answer', pcRef.current.localDescription?.toJSON());
  }, [getMicStream, sendSignal, teardown]);

  const rejectCall = useCallback(async () => {
    if (statusRef.current !== 'incoming') return;
    await sendSignal('call-reject');
    teardown();
  }, [sendSignal, teardown]);

  const endCall = useCallback(async () => {
    const wasLive = statusRef.current === 'connected' || statusRef.current === 'outgoing';
    await sendSignal('call-end');
    if (wasLive) CallAudioService.playCallEnded();
    teardown('connected');
  }, [sendSignal, teardown]);

  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const next = !isMuted;
    localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setIsMuted(next);
  }, [isMuted]);

  const toggleHold = useCallback(async () => {
    if (statusRef.current !== 'connected' || !localStreamRef.current) return;
    const next = !isOnHold;
    // Mute/unmute our outgoing tracks
    localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = isOnHold; }); // enabled = !next
    await sendSignal(next ? 'call-hold' : 'call-unhold');
    setIsOnHold(next);
  }, [isOnHold, sendSignal]);

  const setAvailability = useCallback((a: Availability) => {
    // Keep localStorage as an instant local cache (avoids a flicker on next load)
    localStorage.setItem('qs-call-availability', a);
    setAvailabilityState(a);
    // Persist to DB so the preference survives new devices / browsers
    supabase
      .from('users')
      .update({ call_availability: a })
      .eq('id', currentUser.id)
      .then(({ error }) => {
        if (error) console.warn('[CallContext] Failed to persist call_availability:', error);
      });
  }, [currentUser.id]);

  const clearMicError = useCallback(() => setMicError(null), []);

  // ── Voicemail ─────────────────────────────────────────────────────────────

  const sendVoicemail = useCallback(async (blob: Blob, durationSeconds: number) => {
    const target = remoteUserRef.current;
    if (!target) return;

    try {
      // 1. Upload to Supabase Storage (voicemails bucket)
      const ext = blob.type.includes('ogg') ? 'ogg' : blob.type.includes('mp4') ? 'mp4' : 'webm';
      const fileName = `${callIdRef.current || uuidv4()}/${currentUser.id}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('voicemails')
        .upload(fileName, blob, { contentType: blob.type || 'audio/webm', upsert: false });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('voicemails').getPublicUrl(fileName);

      // 2. Find or create the DM chat between the two users
      const chats = await MessagingService.getChats(currentUser.id);
      let chatId = chats.find((c) =>
        c.type === 'direct' &&
        Array.isArray((c as any).participants) &&
        (c as any).participants.some((p: any) => (p.user_id ?? p.id) === target.id)
      )?.id;

      if (!chatId) {
        chatId = await MessagingService.createDirectChat([currentUser.id, target.id]) ?? undefined;
      }

      if (!chatId) throw new Error('Could not find or create conversation');

      // 3. Send the voicemail as an audio message
      await MessagingService.sendMessage(chatId, currentUser.id, JSON.stringify({
        type:     'audio',
        url:      urlData.publicUrl,
        duration: durationSeconds,
        name:     'Voicemail',
        mimeType: `audio/${ext}`,
      }));

      toast.success('Voicemail sent');
    } catch (err) {
      console.error('[CallContext] Failed to send voicemail:', err);
      toast.error('Failed to send voicemail');
    } finally {
      teardown('no_answer');
    }
  }, [currentUser, teardown]);

  const cancelVoicemail = useCallback(() => {
    teardown('no_answer');
  }, [teardown]);

  // Cleanup on unmount
  useEffect(() => () => { teardown(); CallSignalingService.unsubscribe(); }, [teardown]);

  return (
    <CallContext.Provider value={{
      status, remoteUser, isMuted, isOnHold, remoteOnHold,
      elapsedSeconds, micError, quality, availability,
      outputDevices, outputDeviceId, sinkIdSupported,
      startCall, setExternalCallActive, answerCall, rejectCall, endCall,
      toggleMute, toggleHold, setAvailability, clearMicError, setOutputDevice,
      sendVoicemail, cancelVoicemail,
    }}>
      {children}
    </CallContext.Provider>
  );
}
